import { useEffect, useState } from 'react';
import { Box } from '@mui/material';

import { CommunicationType, IOneClickCommunicationDataModel } from '@modules/OneClickEdit/interfaces';

import { TextEditor } from '@shared/components/TextEditor';
import { Modal } from '@shared/components/modals/Modal';

import { IEmailTemplate, IEmailTemplates, ISMSTemplate, ISMSTemplates } from '@modules/admin/Tools/CustomerCommunications/types';

import { PreviewLabel, ReadOnlyPreview } from '@modules/admin/Tools/CustomerCommunications/components/EditCommunicationForm';

export interface IOneClickPayload {
  communicationType: number;
  daysRangeBegin: number;
  daysRangeEnd: number;
  templateId: string;
  id?: string;
  userId: string | null;
}

interface EditOneClickModalProps {
  oneClickData: IOneClickCommunicationDataModel | null;
  onClose: () => void;
  isOpen: boolean;
  onSubmit: (oneClickPayload: IOneClickPayload) => void;
  editEmailModalPosition: number;
  smsTemplates: ISMSTemplates | undefined;
  emailTemplates: IEmailTemplates | undefined;
  communicationTypeId: CommunicationType;
}

export const EditOneClickModal = ({
  oneClickData,
  onClose,
  isOpen,
  editEmailModalPosition,
  smsTemplates,
  emailTemplates,
  communicationTypeId,
}: EditOneClickModalProps) => {
  const [selectedTemplate, setSelectedTemplate] = useState<IEmailTemplate | ISMSTemplate | null>(null);

  const isEmailTemplate = (template: IEmailTemplate | ISMSTemplate | null): template is IEmailTemplate =>
    !!template && 'templateData' in template;

  const isSmsTemplate = (template: IEmailTemplate | ISMSTemplate | null): template is ISMSTemplate =>
    !!template && 'body' in template;

  useEffect(() => {
    if (communicationTypeId === CommunicationType.Email) {
      const template = emailTemplates?.find((t) => t.id === oneClickData?.template?.id) ?? null;
      setSelectedTemplate(template);
    }

    if (communicationTypeId === CommunicationType.Text) {
      const template = smsTemplates?.find((t) => t.id === oneClickData?.template?.id) ?? null;
      setSelectedTemplate(template);
    }
  }, [oneClickData, communicationTypeId, emailTemplates, smsTemplates]);

  const communicationTypeLabel = communicationTypeId === CommunicationType.Email ? 'Email' : 'Text';

  return (
    <Modal
      hideBackdrop
      dialogWidth="80%"
      dialogMaxWidth="1050px"
      open={isOpen}
      resetPosition={editEmailModalPosition}
      title={`${communicationTypeLabel}`}
      onClose={onClose}
    >
      <>
        <Box
          width="100%"
          mb={2}
        >
          <PreviewLabel>Template Name</PreviewLabel>
          <ReadOnlyPreview style={{ marginBottom: '2rem' }}>{selectedTemplate?.name}</ReadOnlyPreview>
        </Box>

        {communicationTypeId === CommunicationType.Email && isEmailTemplate(selectedTemplate) && (
          <>
            <PreviewLabel>Email subject</PreviewLabel>
            <ReadOnlyPreview style={{ marginBottom: '2rem' }}>{selectedTemplate.subject}</ReadOnlyPreview>
            <TextEditor
              text={selectedTemplate.templateData}
              label="Email Template"
              disable={true}
            />
          </>
        )}

        {communicationTypeId === CommunicationType.Text && isSmsTemplate(selectedTemplate) && (
          <>
            <PreviewLabel>SMS Template</PreviewLabel>
            <ReadOnlyPreview>{selectedTemplate.body}</ReadOnlyPreview>
          </>
        )}
      </>
    </Modal>
  );
};
